import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { A, pipe } from "@mobily/ts-belt"
import type { Group } from "@shared/models/group"
import type { User } from "@shared/models/user"
import groupUnlockOpenFormAction from "cc-frontend/lib/actions/group/unlock/GROUP_UNLOCK_OPEN_FORM"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type ModalManager from "cc-frontend/services/modal-manager"
import {
  isGroupMembershipAccepted,
  isGroupMembershipPotential,
  isGroupMembershipRequested,
} from "cc-frontend/utils/group"
import styles from "./locked-group.module.css"

interface Sig {
  Args: {
    group: Group
    user: User
  }
}

export default class LockedGroup extends Component<Sig> {
  readonly styles = styles

  @service declare modalManager: ModalManager
  @service declare dispatcher: Dispatcher

  @computed("args.group.attributes.memberships.[]")
  get colleagueCount() {
    return pipe(
      this.args.group.attributes.memberships,
      A.filter(
        (membership) =>
          isGroupMembershipAccepted(membership) ||
          isGroupMembershipRequested(membership) ||
          isGroupMembershipPotential(membership)
      ),
      A.reject((membership) => membership.attributes.userId === this.args.user.id),
      A.map((membership) => membership.attributes.userId),
      A.uniq
    ).length
  }

  @action unlock() {
    let form: "teacher" | "admin" | "district-admin"
    if (this.args.user.attributes.educationRole === "district-admin") {
      this.modalManager.openModal("user/unlock-group-form/district-admin", {
        user: this.args.user,
        group: this.args.group,
      })
      form = "district-admin"
    } else if (this.args.user.attributes.educationRole === "admin") {
      this.modalManager.openModal("user/unlock-group-form/admin", {
        user: this.args.user,
        group: this.args.group,
      })
      form = "admin"
    } else {
      this.modalManager.openModal("user/unlock-group-form/teacher", {
        user: this.args.user,
        group: this.args.group,
      })
      form = "teacher"
    }

    this.dispatcher.dispatchPreparedAction(
      groupUnlockOpenFormAction({
        form,
        group: this.args.group,
      })
    )
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Users::Home::LockedGroup": typeof LockedGroup
  }
}
